/**
 * Portal wrapper around @gestionalo/enterprise-graph.
 *
 * Re-exports the canonical graph types + logic and adds the portal-only
 * builder helpers (makeStartNode, makeEndNode, defaultDataForNodeType, …)
 * that the visual editor needs but the backend doesn't.
 */
import type {
  ProductGraph,
  ProductNode,
  ProductNodeType,
  ValidationError,
} from "@gestionalo/enterprise-graph";

export * from "@gestionalo/enterprise-graph";

// Portal historically called it GraphValidationError. Keep the alias.
export type GraphValidationError = ValidationError;

// Portal-only: result shape that computeAvailableFieldsAt returns. Kept as
// a named interface because several editors type their props against it.
export interface AvailableField {
  key: string;
  label: string;
  type: string;
  options?: Array<{ value: string; label: string }>;
}

// ── Builder helpers ──────────────────────────────────────────────────────

export function makeId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function makeStartNode(): ProductNode {
  return {
    id: "start",
    type: "start",
    position: { x: 80, y: 80 },
    data: { label: "" },
  };
}

export function makeEndNode(position?: { x: number; y: number }): ProductNode {
  return {
    id: makeId("end"),
    type: "end",
    position: position ?? { x: 80, y: 360 },
    data: { label: "" },
  };
}

export function makeEmptyGraph(): ProductGraph {
  const start = makeStartNode();
  const end = makeEndNode({ x: 80, y: 360 });
  return {
    version: 1,
    nodes: [start, end],
    edges: [
      {
        id: makeId("e"),
        source: start.id,
        target: end.id,
      },
    ],
  };
}

export function defaultDataForNodeType(type: ProductNodeType): Record<string, unknown> {
  switch (type) {
    case "start":
      return { label: "" };
    case "precondition":
      return {
        label: "",
        description: "",
        fields: [],
        rules: [],
        passMessage: "",
        failMessage: "",
      };
    case "userForm":
      return {
        label: "",
        description: "",
        fields: [],
      };
    case "staffApproval":
      return {
        label: "",
        description: "",
        actor: "tenant",
        slaHours: 48,
      };
    case "staffAction":
      return {
        label: "",
        description: "",
        actor: "tenant",
        slaHours: 48,
        fields: [],
      };
    case "branch":
      return {
        label: "",
        field: "",
        cases: [],
      };
    case "pricing":
      return {
        label: "",
        formula: {
          type: "rate_table",
          baseFields: [],
          profileField: "",
          rates: [],
          installmentTiers: [],
          resultLabel: "",
        },
      };
    case "internalRequest":
      return {
        label: "",
        description: "",
        requestType: "platform_request",
        productFilter: {
          flowKey: "",
          targetTypes: [],
        },
      };
    case "inviteActors":
      return { label: "Invitar participantes", maxActors: 2 };
    case "decision":
      return {
        label: "",
        description: "",
        outputKey: "",
        options: [],
      };
    case "createRequest":
      return { label: "Crear solicitud", description: "" };
    case "automation":
      return {
        label: "",
        automationType: "bcu_credit",
        triggerMode: "manual",
        inputMappings: {},
        retryOnFailure: true,
      };
    case "documentCollection":
      return {
        label: "",
        description: "",
        conditionField: "",
        documentSets: {},
      };
    case "end":
      return { label: "" };
    default:
      return {};
  }
}
